import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { MaskLines, FadeUp, Eyebrow } from "@/components/site/Motion";
import { PhoneFrame } from "@/components/site/PhoneFrame";
import { StoreButtons } from "@/components/site/StoreButtons";
import { CtaBand } from "@/components/site/CtaBand";
import { APPS } from "@/lib/content";

const Gallery = ({ app }) => (
  <section data-testid="app-gallery" className={`relative overflow-hidden bg-gradient-to-br ${app.tint} py-20 sm:py-28`}>
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="-mx-5 flex snap-x snap-mandatory gap-6 overflow-x-auto px-5 pb-4 sm:mx-0 sm:grid sm:grid-cols-3 sm:gap-8 sm:overflow-visible sm:px-0">
        {app.screens.map((s, i) => (
          <FadeUp key={s.src} delay={i * 0.1} className={`w-[72%] shrink-0 snap-center sm:w-auto ${i === 1 ? "sm:translate-y-12" : ""}`}>
            <PhoneFrame src={s.src} alt={`${app.name}: ${s.caption}`} testId={`gallery-screen-${i}`} className="mx-auto max-w-[300px] transition-transform duration-500 hover:-translate-y-2" />
            <p className={`mt-5 text-center text-sm font-medium ${app.id === "trailer-api" ? "text-slate-200" : "text-slate-700"}`}>{s.caption}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Features = ({ app }) => (
  <section data-testid="app-features" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
    <FadeUp>
      <Eyebrow>Features</Eyebrow>
      <h2 className="font-display mt-5 text-3xl font-extrabold tracking-tight text-[#0B132B] sm:text-4xl lg:text-5xl">Everything {app.name} does.</h2>
    </FadeUp>
    <div className="mt-12 grid gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 sm:grid-cols-2">
      {app.features.map((f, i) => (
        <FadeUp key={f} delay={(i % 4) * 0.05} className="flex items-start gap-4 bg-white p-7">
          <span className="font-mono text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</span>
          <span className="flex items-start gap-3 text-base text-[#0B132B]">
            <Check className="mt-1 h-4 w-4 shrink-0 text-[#0D6EFD]" />{f}
          </span>
        </FadeUp>
      ))}
    </div>
  </section>
);

export default function AppDetail() {
  const { id } = useParams();
  const app = APPS.find((a) => a.id === id);
  if (!app) return <Navigate to="/portfolio" replace />;
  const other = APPS.find((a) => a.id !== id);
  return (
    <div data-testid={`app-detail-${app.id}`}>
      <section className="relative overflow-hidden pb-16 pt-32 sm:pt-40">
        <div className="grid-lines absolute inset-0 [mask-image:radial-gradient(ellipse_at_top_left,black_30%,transparent_75%)]" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Link to="/portfolio" data-testid="app-detail-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#0D6EFD]"><ArrowLeft className="h-4 w-4" /> All apps</Link>
          <div className="mt-8"><Eyebrow>{app.kind} · {app.platforms.join(" & ")}</Eyebrow></div>
          <h1 className="font-display mt-6 text-5xl font-extrabold leading-[0.95] tracking-tight text-[#0B132B] sm:text-6xl lg:text-7xl">
            <MaskLines lines={[app.name, <span className="text-[#0D6EFD]">{app.tagline}</span>]} delay={0.1} />
          </h1>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5 }}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">{app.description}</p>
            <div className="mt-8"><StoreButtons app={app} /></div>
          </motion.div>
        </div>
      </section>
      <Gallery app={app} />
      <Features app={app} />
      {other && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8">
          <Link to={`/portfolio/${other.id}`} data-testid="app-detail-next-link" className="group flex items-center justify-between gap-6 rounded-3xl border border-slate-200 bg-white p-8 transition-colors hover:border-[#0D6EFD]">
            <span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Next app</span>
              <span className="font-display mt-2 block text-3xl font-extrabold text-[#0B132B]">{other.name}</span>
            </span>
            <ArrowUpRight className="h-8 w-8 text-[#0D6EFD] transition-transform duration-300 group-hover:rotate-45" />
          </Link>
        </section>
      )}
      <CtaBand title={`Questions about ${app.name}?`} text="Send us a message and we will get back to you." />
    </div>
  );
}
